import {
  type AppliJeu,
  ECART_ORDRE_JEU,
  type LivreJeuDetail,
  type PublicationJeu,
} from "./contract";
import { quandEnClair } from "./dates-en-clair";
import {
  ALERTE_GRISE,
  ALERTE_JAUNE,
  ALERTE_PANNE,
  ALERTE_VERTE,
  BOUTON_CONTOUR,
  Carte,
  LIEN_BOUTON_PLEIN,
  Rapport,
} from "./livres-jeux-commun";
import { messageDEchec, RefusJeu, useAppliJeu, usePublier } from "./livres-jeux-requetes";

/**
 * L'onglet Publication (spec du 03/10/2026, D7) : publier l'edition telephone du livre dans
 * l'appli compagnon, ce que l'appli sert vraiment, et la derniere publication du livre. Quand
 * des pages ou des codes changent depuis la derniere publication recue, la console le dit et
 * demande de confirmer : un livre deja imprime ne correspondrait plus.
 */

const empreinteCourte = (empreinte: string): string => empreinte.slice(0, 12);

export function OngletPublication({ detail }: { detail: LivreJeuDetail }) {
  const { livre, publication } = detail;
  const publier = usePublier(livre.id);
  const appli = useAppliJeu(true);
  const refus = publier.error instanceof RefusJeu ? publier.error : null;
  // A confirmer des que la structure change, meme sans ecart lisible : le bouton est toujours la.
  const aConfirmer = refus?.code === "structure_changee";
  const ecarts = aConfirmer ? refus.ecarts : [];

  return (
    <div className="grid items-start gap-5 lg:grid-cols-2">
      <Carte id="livres-jeux-publier" titre="Publier dans l'appli">
        <p className="text-sm text-muted-foreground">
          L'appli compagnon reçoit l'édition téléphone : les codes, les pages, les choix, sans aucun
          texte du livre. Elle ne sert qu'un livre à la fois : publier celui-ci remplace celui
          qu'elle sert.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            className={LIEN_BOUTON_PLEIN}
            disabled={publier.isPending || detail.scenes.length === 0}
            onClick={() => publier.mutate({ confirmer_structure: false })}
            type="button"
          >
            {publier.isPending ? "Publication..." : "Publier"}
          </button>
        </div>
        {!aConfirmer ? null : (
          <div className={ALERTE_JAUNE} role="alert">
            <p className="font-semibold">{refus?.message}</p>
            {ecarts.length === 0 ? null : (
              <ul className="my-2 flex flex-col gap-1 [overflow-wrap:anywhere]">
                {ecarts.map((ecart) =>
                  ecart.scene === ECART_ORDRE_JEU ? (
                    <li key={ecart.scene}>
                      L'ordre des scènes : {ecart.avant} → {ecart.apres}
                    </li>
                  ) : (
                    <li key={ecart.scene}>
                      <span className="font-mono text-xs">{ecart.scene}</span> :{" "}
                      {ecart.avant ?? "nouvelle"} → {ecart.apres ?? "retirée"}
                    </li>
                  ),
                )}
              </ul>
            )}
            <button
              className={BOUTON_CONTOUR}
              disabled={publier.isPending}
              onClick={() => publier.mutate({ confirmer_structure: true })}
              type="button"
            >
              Publier quand même
            </button>
          </div>
        )}
        {refus?.rapport ? <Rapport rapport={refus.rapport} /> : null}
        {publier.isError && !aConfirmer && !refus?.rapport ? (
          <p className={ALERTE_PANNE} role="alert">
            {messageDEchec(publier.error)}
          </p>
        ) : null}
        {publier.data ? <ResultatPublication publication={publier.data} /> : null}
      </Carte>

      <Carte id="livres-jeux-appli" titre="Ce que sert l'appli">
        <CeQueSertLAppli
          appli={appli.data}
          cle={livre.cle}
          erreur={appli.isError ? appli.error : null}
        />
        {publication === null ? (
          <p className="text-sm text-muted-foreground">Ce livre n'a jamais été publié.</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Dernière publication de ce livre : {quandEnClair(publication.publie_le, Date.now())},{" "}
            {publication.recu
              ? `reçue par l'appli (empreinte ${empreinteCourte(publication.empreinte)}).`
              : `refusée : ${publication.reponse ?? "sans réponse"}.`}
          </p>
        )}
        <div>
          <button
            className={BOUTON_CONTOUR}
            disabled={appli.isFetching}
            onClick={() => void appli.refetch()}
            type="button"
          >
            Relire l'appli
          </button>
        </div>
      </Carte>
    </div>
  );
}

function ResultatPublication({ publication }: { publication: PublicationJeu }) {
  return publication.recu ? (
    <p className={ALERTE_VERTE} role="status">
      Publié : l'appli sert maintenant ce livre (empreinte {empreinteCourte(publication.empreinte)}
      ).
    </p>
  ) : (
    <p className={ALERTE_PANNE} role="alert">
      L'appli n'a pas pris le livre : {publication.reponse ?? "sans réponse"}. La publication est
      gardée ; republie quand l'appli répond.
    </p>
  );
}

function CeQueSertLAppli({
  appli,
  cle,
  erreur,
}: {
  appli: AppliJeu | undefined;
  cle: string;
  erreur: Error | null;
}) {
  if (erreur !== null) {
    return (
      <p className={ALERTE_PANNE} role="alert">
        {messageDEchec(erreur)}
      </p>
    );
  }
  if (appli === undefined) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Lecture de l'appli...
      </p>
    );
  }
  if (appli.etat === "rien") {
    return (
      <p className={ALERTE_GRISE}>L'appli ne sert aucun livre publié : elle montre sa démo.</p>
    );
  }
  if (appli.etat === "injoignable") {
    return <p className={ALERTE_PANNE}>{appli.message}</p>;
  }
  return (
    <p className={appli.livre === cle ? ALERTE_VERTE : ALERTE_GRISE}>
      <strong className="font-semibold">{appli.titre}</strong>{" "}
      <span className="font-mono text-xs">({appli.livre})</span>, publié{" "}
      {quandEnClair(appli.publie_le, Date.now())}, empreinte {empreinteCourte(appli.empreinte)}
      {appli.livre === cle ? " : c'est ce livre." : " : un autre livre."}
    </p>
  );
}
